import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Chip,
  IconButton, Stack, Select, MenuItem, FormControl, InputLabel,
  Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Skeleton, Checkbox, Menu,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  People as PeopleIcon,
  NewReleases as NewReleasesIcon,
  Phone as PhoneIcon,
  Gavel as GavelIcon,
  RequestQuote as QuoteIcon,
  EventAvailable as BookingIcon,
  CheckCircle as ConfirmedIcon,
  Cancel as LostIcon,
  Whatshot as HotIcon,
  WbSunny as WarmIcon,
  AcUnit as ColdIcon,
  PersonOff as UnassignedIcon,
  WorkspacePremium as VipIcon,
  NotificationsActive as FollowUpIcon,
  SwapHoriz as StatusChangeIcon,
  ArrowDropDown as DropdownIcon,
  Close as CloseIcon,
  FlashOn as TodaysWorkIcon,
  Event as EventDateIcon,
  CalendarToday as LeadDateIcon,
} from "@mui/icons-material";
import {
  fetchLeadsWithMeta, fetchDashboardStats, updateLeadStatus, addLeadNote, fetchTeamUsers,
  claimLead, checkLeadChanges,
  type LeadThread, type LeadStatus, type PaginationMeta, type TeamUser,
  STATUS_CONFIG, AI_SCORE_CONFIG,
} from "./leadApi";
import { emitLeadCreated } from "./leadBus";
import { usePermission } from "../../hooks/usePermission";
import { useLeadBus } from "../../hooks/useLeadBus";
import { useNotifier } from "../../Core/Notifier";
import { ContactActionsProvider } from "../../Components/ContactActions";
import LeadThreadDrawer  from "./LeadThreadDrawer";
import CreateLeadDialog  from "./CreateLeadDialog";
import PremiumDatePicker from "../../Components/PremiumDatePicker";
import { CustomerCell }  from "./CustomerCell";
import { EventCell }     from "./EventCell";
import { LeadInfoCell }  from "./LeadInfoCell";
import { ScoreSourceCell } from "./ScoreSourceCell";
import { AssignedCell }  from "./AssignedCell";
import { NextActionCell } from "./NextActionCell";
import {
  NAVY, GOLD, avatarColor, todayStr,
  SOURCE_CFG,
} from "./leadCellUtils";

/* ═══════════════════════════════════════════════════════════════════════════
   QUICK-DATE HELPERS
═══════════════════════════════════════════════════════════════════════════ */
type QuickDate = "today" | "yesterday" | "week" | "month" | null;
type DateField = "created_at" | "event_date";
type LeadView = "all" | "my" | "unassigned" | "assigned" | "followups" | "hot" | "previously_assigned";

/** Full set of view tabs — used by Admin / Sales Manager who can see
 *  every lead in the system. Includes the "Previously Assigned"
 *  read-only bucket so admins can audit reassignment outcomes. */
const LEAD_VIEW_OPTIONS_FULL: { key: LeadView; label: string }[] = [
  { key: "all",                 label: "All Leads"            },
  { key: "my",                  label: "My Leads"             },
  { key: "unassigned",          label: "Unassigned"           },
  { key: "assigned",            label: "Assigned"             },
  { key: "previously_assigned", label: "Previously Assigned"  },
];

/** Restricted set for Lead Managers / Receptionists / other non-admin
 *  roles. They cannot meaningfully see "All Leads" (visibility is
 *  already scoped server-side to their own queue + unassigned pool),
 *  and "Assigned" is irrelevant since they can't view assignments to
 *  other staff. Per the enterprise spec: My / Unassigned / Follow-ups
 *  / Hot. The "Previously Assigned" tab gives a read-only audit window
 *  into the leads they used to own. */
const LEAD_VIEW_OPTIONS_RESTRICTED: { key: LeadView; label: string }[] = [
  { key: "my",                  label: "My Leads"             },
  { key: "unassigned",          label: "Unassigned"           },
  { key: "followups",           label: "Follow-ups"           },
  { key: "hot",                 label: "Hot Leads"            },
  { key: "previously_assigned", label: "Previously Assigned"  },
];

const QUICK_DATE_OPTIONS: { key: Exclude<QuickDate, null>; label: string }[] = [
  { key: "today",     label: "Today"      },
  { key: "yesterday", label: "Yesterday"  },
  { key: "week",      label: "This Week"  },
  { key: "month",     label: "This Month" },
];

// Local-date formatter (YYYY-MM-DD). NOT toISOString() — that returns the
// UTC date, which rolls forward after ~8 PM EDT and would make the "Today"
// filter query tomorrow's date. The backend interprets these strings against
// app.timezone (America/Toronto) via whereDate(), so the wall-clock day the
// user sees in their browser must match what we send.
const fmtIso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function fmtChipDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function computeDateRange(key: Exclude<QuickDate, null | "custom">): { from: string; to: string } {
  const now = new Date(), t = fmtIso(now);
  if (key === "today")     return { from: t, to: t };
  if (key === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); const s = fmtIso(y); return { from: s, to: s }; }
  if (key === "week")      { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return { from: fmtIso(s), to: t }; }
  if (key === "month")     { return { from: fmtIso(new Date(now.getFullYear(), now.getMonth(), 1)), to: t }; }
  return { from: t, to: t };
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI TYPES
═══════════════════════════════════════════════════════════════════════════ */
type KpiKey =
  | "total" | "new" | "contacted" | "negotiation"
  | "ready_for_quote" | "ready_for_booking" | "confirmed" | "lost"
  | "hot" | "warm" | "cold" | "unassigned" | "vip" | "follow_up_today";

interface KpiDef {
  key: KpiKey; label: string; sub: string;
  icon: React.ReactNode; accent: string; bg: string; border: string; value: number;
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════════════════════════════════════ */
const KpiCard: React.FC<{ def: KpiDef; active: boolean; loading: boolean; onClick: () => void }> = ({
  def, active, loading, onClick,
}) => (
  <Box onClick={onClick} sx={{
    position: "relative", flex: "1 1 0", minWidth: 108, maxWidth: 160,
    bgcolor: active ? def.accent : "#fff",
    border: `1.5px solid ${active ? def.accent : def.border}`,
    borderRadius: "16px", px: 2, py: 1.5, cursor: "pointer", overflow: "hidden",
    transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
    boxShadow: active ? `0 4px 18px ${def.accent}40` : "0 1px 4px rgba(0,0,0,0.06)",
    "&:hover": { borderColor: def.accent, boxShadow: `0 6px 22px ${def.accent}30`, transform: "translateY(-2px)" },
    "&::before": active ? { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, borderRadius: "16px 16px 0 0", bgcolor: def.accent } : {},
  }}>
    <Box sx={{ position: "absolute", top: -8, right: -8, width: 52, height: 52, borderRadius: "50%", bgcolor: active ? "rgba(255,255,255,0.15)" : `${def.accent}12` }} />
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={0.5}>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: active ? "rgba(255,255,255,0.2)" : def.bg, display: "flex", alignItems: "center", justifyContent: "center", color: active ? "#fff" : def.accent, "& svg": { fontSize: 17 }, flexShrink: 0 }}>
        {def.icon}
      </Box>
    </Stack>
    {loading ? <Skeleton variant="text" width={40} height={36} /> : (
      <Typography variant="h5" fontWeight={800} lineHeight={1.1} color={active ? "#fff" : NAVY}
        sx={{ fontSize: "1.45rem", letterSpacing: "-0.5px", mt: 0.3 }}>
        {def.value.toLocaleString()}
      </Typography>
    )}
    <Typography variant="caption" fontWeight={700} color={active ? "rgba(255,255,255,0.9)" : "#374151"}
      display="block" mt={0.3} sx={{ fontSize: "0.68rem", lineHeight: 1.2 }}>
      {def.label}
    </Typography>
    <Typography variant="caption" color={active ? "rgba(255,255,255,0.65)" : "#94a3b8"} sx={{ fontSize: "0.6rem" }}>
      {def.sub}
    </Typography>
  </Box>
);

const RowLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" sx={{ color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 0.8, mt: 0.5 }}>
    {children}
  </Typography>
);

const FilterSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap", alignSelf: "center" }}>
    {children}
  </Typography>
);

/* ═══════════════════════════════════════════════════════════════════════════
   TABLE COLUMN HEADERS
═══════════════════════════════════════════════════════════════════════════ */
const COL_HEADERS: { label: string; minWidth: number; align?: "left" | "center" }[] = [
  { label: "Customer",    minWidth: 220 },
  { label: "Event",       minWidth: 180 },
  { label: "Lead Info",   minWidth: 210 },
  { label: "Score",       minWidth: 120 },
  { label: "Assigned",    minWidth: 130 },
  { label: "Next Action", minWidth: 170 },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function LeadInquiries() {
  /* Permission context — drives the visible tab set and the
     reassignment / claim affordances. Admins + Sales Managers see
     every lead and the full 4-tab view; everyone else (Lead Manager
     etc.) sees the restricted set and the "Assign to Me" button on
     unassigned rows. */
  const { userId: currentUserId, canViewAllLeads, isAdmin } = usePermission();
  const viewOptions = canViewAllLeads ? LEAD_VIEW_OPTIONS_FULL : LEAD_VIEW_OPTIONS_RESTRICTED;

  const [leads, setLeads]               = useState<LeadThread[]>([]);
  const [meta, setMeta]                 = useState<PaginationMeta | null>(null);
  const [loading, setLoading]           = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats]               = useState<Record<string, any>>({});
  const [teamUsers, setTeamUsers]       = useState<TeamUser[]>([]);

  /* ── Filters ─────────────────────────────────────────────────────────── */
  const [search, setSearch]                   = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter]       = useState<string>("");
  const [sourceFilter, setSourceFilter]       = useState<string>("");
  const [scoreFilter, setScoreFilter]         = useState<string>("");
  const [assigneeFilter, setAssigneeFilter]   = useState<string>("");
  const [dateFrom, setDateFrom]               = useState<string>("");
  const [dateTo, setDateTo]                   = useState<string>("");
  const [showUnassigned, setShowUnassigned]   = useState(false);
  const [todaysWork, setTodaysWork]           = useState(false);
  const [activeKpi, setActiveKpi]             = useState<KpiKey>("total");
  const [page, setPage]                       = useState(0);
  const [perPage, setPerPage]                 = useState(50);

  /* ── Date mode & quick date ──────────────────────────────────────────── */
  const [dateField, setDateField] = useState<DateField>("created_at");
  const [quickDate, setQuickDate] = useState<QuickDate>(null);

  /* ── Lead visibility view ─────────────────────────────────────────────
     Admin / Sales Manager → defaults to "all" (full company list).
     Everyone else → defaults to "my" (their personal queue), since
     "All" isn't an option in the restricted tab set. The URL `?view=`
     param wins (e.g. the dashboard navigates with
     `?view=previously_assigned` for the Previously Assigned section). */
  const initialView = ((): LeadView => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search).get("view") as LeadView | null;
      const allowed: LeadView[] = ["all","my","unassigned","assigned","followups","hot","previously_assigned"];
      if (p && allowed.includes(p)) return p;
    }
    return canViewAllLeads ? "all" : "my";
  })();
  const [leadView, setLeadView] = useState<LeadView>(initialView);

  /* ── Self-assign in flight (per-row) ───────────────────────────────────
     Tracks which lead is currently being claimed so the button can show
     "Claiming…" and ignore double-clicks. Cleared on success or error. */
  const [claimingId, setClaimingId] = useState<number | null>(null);

  const handleClaim = async (leadId: number) => {
    setClaimingId(leadId);
    try {
      const updated = await claimLead(leadId);
      // Optimistic: update the row in place so the operator sees their
      // name immediately. The next refetch (loadLeads) will normalize.
      setLeads(prev => prev.map(l => l.id === leadId
        ? { ...l, assigned_to: updated.assigned_to ?? null, assignee: (updated as any).assignee ?? l.assignee }
        : l));
      // If we're currently in the Unassigned tab, the lead should
      // disappear from view per the enterprise spec — reload to drop it.
      if (leadView === "unassigned") {
        loadLeads(activeKpi);
      }
      loadStats();
    } catch (e: any) {
      // 409 = race (someone else claimed first). Refresh the list so
      // the operator sees the up-to-date queue.
      if (e?.status === 409) loadLeads(activeKpi);
    }
    setClaimingId(null);
  };

  /* ── Live update (head-check poll) ────────────────────────────────────
     `freshIds` carries the ids the latest poll discovered. The table
     row renderer paints those rows with a soft amber background that
     fades back to neutral after ~3 seconds. `latestKnown` tracks the
     highest id + max updated_at the client has on screen, so the next
     poll only counts truly new / changed rows.

     The poll endpoint is intentionally lightweight: even at a 12s
     cadence it costs the DB ~2 COUNT(*) queries and a small SELECT
     LIMIT 3 — far cheaper than refetching the full paginated list. We
     only fire the heavy refetch when has_changes=true. */
  const [freshIds, setFreshIds] = useState<Set<number>>(new Set());
  const latestKnownRef = useRef<{ id: number; updatedAt: string | null }>({ id: 0, updatedAt: null });
  // Stable Notifier handle for the toast. Pulling it once at module top
  // avoids re-render churn inside the polling interval.
  const { showMessage } = useNotifier();

  /* ── Selection & interaction ─────────────────────────────────────────── */
  const [selected, setSelected]             = useState<Set<number>>(new Set());
  const [hoveredRow, setHoveredRow]         = useState<number | null>(null);
  const [bulkStatusMenu, setBulkStatusMenu] = useState<HTMLElement | null>(null);
  const [noteDialog, setNoteDialog]         = useState<{ open: boolean; leadId: number | null }>({ open: false, leadId: null });
  const [noteText, setNoteText]             = useState("");
  const [noteLoading, setNoteLoading]       = useState(false);

  /* ── Drawer / create ─────────────────────────────────────────────────── */
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Clear all ───────────────────────────────────────────────────────── */
  const clearAll = useCallback(() => {
    setStatusFilter(""); setSourceFilter(""); setScoreFilter(""); setAssigneeFilter("");
    setDateFrom(""); setDateTo(""); setShowUnassigned(false); setTodaysWork(false);
    setSearch(""); setDebouncedSearch(""); setPage(0); setActiveKpi("total");
    setQuickDate(null); setDateField("created_at"); setLeadView("all");
  }, []);

  /* ── KPI filters ─────────────────────────────────────────────────────── */
  const kpiFilters = useCallback((kpi: KpiKey): Record<string, any> => {
    switch (kpi) {
      case "new":               return { status: "new" };
      case "contacted":         return { status: "contacted" };
      case "negotiation":       return { status: "negotiation" };
      case "ready_for_quote":   return { status: "ready_for_quote" };
      case "ready_for_booking": return { status: "ready_for_booking" };
      case "confirmed":         return { status: "confirmed" };
      case "lost":              return { status: "lost" };
      case "hot":               return { ai_score: "hot" };
      case "warm":              return { ai_score: "warm" };
      case "cold":              return { ai_score: "cold" };
      case "unassigned":        return { unassigned: "1" };
      case "vip":               return { is_vip: "1" };
      case "follow_up_today":   return { follow_up_today: "1" };
      default:                  return {};
    }
  }, []);

  /* ── Load leads ──────────────────────────────────────────────────────── */
  const loadLeads = useCallback(async (kpi: KpiKey) => {
    setLoading(true);
    try {
      const f: Record<string, any> = { page: page + 1, per_page: perPage, ...kpiFilters(kpi) };
      if (!f.status && statusFilter)  f.status          = statusFilter;
      if (sourceFilter)               f.source          = sourceFilter;
      if (scoreFilter)                f.ai_score        = scoreFilter;
      if (assigneeFilter)             f.assigned_to     = assigneeFilter;
      if (dateFrom)                   f.date_from       = dateFrom;
      if (dateTo)                     f.date_to         = dateTo;
      if (dateFrom || dateTo)         f.date_field      = dateField;
      if (showUnassigned)             f.unassigned      = "1";
      if (todaysWork)                 f.follow_up_today = "1";

      // ── Map the leadView tab to backend query params ──
      //   "all" / "my" / "unassigned" / "assigned" → simply forward as
      //                                              the `view` param.
      //   "followups" → my leads + follow_up_required=1
      //   "hot"       → my leads + ai_score=hot
      if (leadView === "followups") {
        f.view = "my";
        f.follow_up_required = "1";
      } else if (leadView === "hot") {
        f.view = "my";
        f.ai_score = "hot";
      } else if (leadView !== "all") {
        f.view = leadView;
      }

      if (debouncedSearch)            f.search          = debouncedSearch;
      const result = await fetchLeadsWithMeta(f);
      setLeads(result.data);
      setMeta(result.meta);
    } catch {}
    setLoading(false);
  }, [page, perPage, debouncedSearch, statusFilter, sourceFilter, scoreFilter, assigneeFilter, dateFrom, dateTo, dateField, showUnassigned, todaysWork, leadView, kpiFilters]);

  /* ── Load stats ──────────────────────────────────────────────────────── */
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats((await fetchDashboardStats()) as any); } catch {}
    setStatsLoading(false);
  }, []);

  useEffect(() => { loadStats(); fetchTeamUsers().then(setTeamUsers).catch(() => {}); }, [loadStats]);

  /* ── Auto-switch to "Unassigned" for empty-queue Lead Managers ──
     If a restricted user (Lead Manager / Receptionist / etc.) lands
     here with zero assigned leads but available unassigned ones, the
     default "My Leads" tab would render an empty table — bad UX. We
     flip the default tab to "Unassigned" on the first successful
     stats load so they immediately see actionable work. Guarded by
     `autoSwitchedRef` so this happens exactly once per session
     (operator's later manual tab choices are never overridden). */
  const autoSwitchedRef = React.useRef(false);
  useEffect(() => {
    if (autoSwitchedRef.current) return;
    if (canViewAllLeads) return;                              // full-access roles keep "all"
    if (statsLoading) return;                                 // wait for stats
    const total      = Number(stats?.total ?? 0);
    const unassigned = Number(stats?.unassigned ?? 0);
    const myCount    = Math.max(0, total - unassigned);
    if (myCount === 0 && unassigned > 0 && leadView === "my") {
      autoSwitchedRef.current = true;
      setLeadView("unassigned");
      setPage(0);
    } else if (total > 0 || unassigned === 0) {
      // Stats are populated and the auto-switch condition didn't
      // apply — mark it as done so we don't re-evaluate forever.
      autoSwitchedRef.current = true;
    }
  }, [statsLoading, stats, canViewAllLeads, leadView]);
  useEffect(() => { loadLeads(activeKpi); }, [loadLeads, activeKpi]);
  useEffect(() => {
    const h = () => { loadStats(); loadLeads(activeKpi); };
    window.addEventListener("focus", h);
    return () => window.removeEventListener("focus", h);
  }, [loadStats, loadLeads, activeKpi]);

  /* ── Cross-surface state sync via leadBus ──────────────────────
     When ANY lead is mutated anywhere in the app (the detail page,
     the pipeline drag-drop, an admin reassignment, a same-event
     flag, etc.) the bus fires and we refresh both the row list and
     the KPI counters. Cross-tab too — see leadBus.ts. */
  useLeadBus(() => {
    loadLeads(activeKpi);
    loadStats();
  });

  /* ── Keep the "highest seen" markers in sync with the rendered list.
     Updated whenever `leads` changes (initial load, head-check refetch,
     bulk mutations). Drives the head-check's since_id / since_updated_at
     so each poll only reports leads created or bumped AFTER what's
     already on screen. */
  useEffect(() => {
    if (!leads.length) return;
    let maxId = 0;
    let maxUpd: string | null = null;
    for (const l of leads) {
      if (l.id > maxId) maxId = l.id;
      const u = (l as any).updated_at as string | undefined;
      if (u && (!maxUpd || u > maxUpd)) maxUpd = u;
    }
    latestKnownRef.current = { id: maxId, updatedAt: maxUpd };
  }, [leads]);

  /* ── Live update poll ─────────────────────────────────────────────
     Polls the cheap /leads/check-changes head-check every 12 seconds.
     The endpoint returns insert / update counts (and a tiny preview)
     within the SAME visibility + filter scope the operator currently
     has on screen, so the toast / row-flash only fire for leads they'd
     actually see in the table.

     Behaviour rules wired in here:
       • Tab hidden → poll is suspended (no requests until visible).
       • Tab regains focus → fires an immediate head-check.
       • Changes detected → full /leads + /dashboard-stats refetch
         (existing `loadLeads` + `loadStats`), toast per new lead (up
         to 3 to avoid spam), and the new ids flash for ~3s.
       • Duplicate prevention: refetch replaces the list wholesale so
         the new ids can't appear twice; the flash Set is overwritten
         each tick, not merged, so a re-seen id won't re-flash later. */
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let inFlight: AbortController | null = null;

    const tick = async () => {
      // Skip while the previous tick is still in flight — prevents
      // request pile-up if the server is briefly slow.
      if (inFlight) return;
      // Don't burn requests on a backgrounded tab. The
      // visibilitychange handler below will trigger an immediate tick
      // when the user returns.
      if (typeof document !== "undefined" && document.hidden) return;

      // Build the same filter object loadLeads would send so the
      // head-check counts match what the operator is currently viewing.
      const f: Record<string, any> = { ...kpiFilters(activeKpi) };
      if (!f.status && statusFilter)  f.status          = statusFilter;
      if (sourceFilter)               f.source          = sourceFilter;
      if (scoreFilter)                f.ai_score        = scoreFilter;
      if (assigneeFilter)             f.assigned_to     = assigneeFilter;
      if (dateFrom)                   f.date_from       = dateFrom;
      if (dateTo)                     f.date_to         = dateTo;
      if (dateFrom || dateTo)         f.date_field      = dateField;
      if (showUnassigned)             f.unassigned      = "1";
      if (todaysWork)                 f.follow_up_today = "1";
      if (leadView === "followups")        { f.view = "my"; f.follow_up_required = "1"; }
      else if (leadView === "hot")         { f.view = "my"; f.ai_score = "hot"; }
      else if (leadView !== "all")         { f.view = leadView; }
      if (debouncedSearch)            f.search          = debouncedSearch;

      const { id: sinceId, updatedAt } = latestKnownRef.current;
      // Don't fire the very first poll until we have at least one row
      // loaded — otherwise sinceId=0 would flag every lead in the
      // system as "new" the first time the page mounts.
      if (sinceId === 0) return;

      inFlight = new AbortController();
      try {
        const res = await checkLeadChanges(f, sinceId, updatedAt, inFlight.signal);
        if (cancelled) return;
        if (!res?.has_changes) return;

        // Trigger the heavy refetch — the leads-changed effect above
        // will rebump latestKnownRef with the new max id / updated_at.
        loadLeads(activeKpi);
        loadStats();

        if (res.new_count > 0) {
          const preview = res.preview ?? [];
          const flashSet = new Set<number>(preview.map(p => p.id));
          setFreshIds(flashSet);
          // Clear flash after 3s so the row settles into neutral.
          setTimeout(() => {
            if (cancelled) return;
            setFreshIds(prev => {
              if (prev.size === 0) return prev;
              // Only remove ids that were in this tick — preserves
              // ids from a later overlapping tick (rare but possible).
              const next = new Set(prev);
              flashSet.forEach(id => next.delete(id));
              return next;
            });
          }, 3000);

          // Toast each new lead (capped at 3 — anything bigger is
          // probably a bulk import; let the table speak for itself).
          for (const p of preview.slice(0, 3)) {
            const who = (p.name || "").trim() || `Lead #${p.id}`;
            const src = p.source ? ` (${p.source})` : "";
            showMessage(`New inquiry received: ${who}${src}`);
            // Mirror onto the leadBus so the pipeline, dashboard, and
            // any other open surface refresh at the same moment.
            emitLeadCreated(p.id);
          }
        }
      } catch {
        // Network blips are routine — silent retry on the next tick.
      } finally {
        inFlight = null;
      }
    };

    intervalId = setInterval(tick, 12_000);

    // Fire an immediate check when the tab regains focus so the user
    // isn't staring at a stale table after switching back.
    const onVisible = () => {
      if (typeof document !== "undefined" && !document.hidden) tick();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      inFlight?.abort();
    };
  }, [
    activeKpi, kpiFilters, statusFilter, sourceFilter, scoreFilter,
    assigneeFilter, dateFrom, dateTo, dateField, showUnassigned,
    todaysWork, leadView, debouncedSearch, loadLeads, loadStats,
    showMessage,
  ]);

  /* ── Handlers ────────────────────────────────────────────────────────── */
  const handleKpi = (key: KpiKey) => {
    setActiveKpi(key); setPage(0); setSelected(new Set());
    if (["new","contacted","negotiation","ready_for_quote","ready_for_booking","confirmed","lost"].includes(key)) setStatusFilter("");
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(val); setPage(0); }, 300);
  };

  const handleQuickDate = (key: Exclude<QuickDate, null>) => {
    if (quickDate === key) { setQuickDate(null); setDateFrom(""); setDateTo(""); setPage(0); return; }
    const r = computeDateRange(key);
    setQuickDate(key); setDateFrom(r.from); setDateTo(r.to); setPage(0);
  };

  const clearDateFilter = () => { setQuickDate(null); setDateFrom(""); setDateTo(""); setPage(0); };

  const handleStatusChange = async (leadId: number, newStatus: LeadStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      loadStats();
    } catch {}
  };

  const handleBulkStatus = async (newStatus: LeadStatus) => {
    setBulkStatusMenu(null);
    await Promise.all(Array.from(selected).map(id => updateLeadStatus(id, newStatus).catch(() => {})));
    setSelected(new Set()); loadLeads(activeKpi); loadStats();
  };

  const handleAddNote = async () => {
    if (!noteDialog.leadId || !noteText.trim()) return;
    setNoteLoading(true);
    try { await addLeadNote(noteDialog.leadId, noteText.trim()); setNoteDialog({ open: false, leadId: null }); setNoteText(""); } catch {}
    setNoteLoading(false);
  };

  /* ── Row selection ───────────────────────────────────────────────────── */
  const isAllSelected   = leads.length > 0 && leads.every(l => selected.has(l.id));
  const isIndeterminate = leads.some(l => selected.has(l.id)) && !isAllSelected;
  const toggleAll = () => {
    if (isAllSelected) setSelected(prev => { const s = new Set(prev); leads.forEach(l => s.delete(l.id)); return s; });
    else               setSelected(prev => { const s = new Set(prev); leads.forEach(l => s.add(l.id)); return s; });
  };
  const toggleRow = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const openThread = (id: number) => {
    // Strict gate — drawer expects a positive integer lead id. If the row
    // mutates to expose thread_id / nested ids in the future, the drawer
    // surfaces a clear console error instead of silently empty-stating.
    if (!Number.isInteger(id) || id <= 0) {
      // eslint-disable-next-line no-console
      console.error("Invalid lead ID passed to drawer", id);
      return;
    }
    // eslint-disable-next-line no-console
    console.log("Opening lead drawer", id);
    setSelectedId(id);
    setDrawerOpen(true);
  };

  /* ── KPI definitions ─────────────────────────────────────────────────── */
  const monthLabel = useMemo(() => new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" }), []);
  const s = stats?.by_status || {};

  const pipelineKpis: KpiDef[] = [
    // For restricted users (Lead Manager / Receptionist / etc.) the
    // first KPI tile is NOT "All Leads" — they don't see company-wide
    // numbers. It's their personal queue plus the unassigned intake
    // pool. Admin / Sales Manager still see "All Leads".
    { key: "total",             label: canViewAllLeads ? "All Leads" : "Visible Leads",       sub: canViewAllLeads ? "entire pipeline" : "your queue + intake",  icon: <PeopleIcon />,      accent: NAVY,      bg: "#f0f4ff", border: "#dbeafe", value: stats.total || 0 },
    { key: "new",               label: "New Inquiry",     sub: "awaiting contact", icon: <NewReleasesIcon />, accent: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", value: s.new || 0 },
    { key: "contacted",         label: "Contacted",       sub: "in conversation",  icon: <PhoneIcon />,       accent: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe", value: s.contacted || 0 },
    { key: "negotiation",       label: "Negotiation",     sub: "discussing terms", icon: <GavelIcon />,       accent: "#f59e0b", bg: "#fffbeb", border: "#fde68a", value: s.negotiation || 0 },
    { key: "ready_for_quote",   label: "Ready for Quote", sub: "quote pending",    icon: <QuoteIcon />,       accent: "#14b8a6", bg: "#f0fdfa", border: "#99f6e4", value: s.ready_for_quote || 0 },
    { key: "ready_for_booking", label: "Booking Ready",   sub: "awaiting deposit", icon: <BookingIcon />,     accent: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0", value: s.ready_for_booking || 0 },
    { key: "confirmed",         label: "Confirmed",       sub: "booking secured",  icon: <ConfirmedIcon />,   accent: "#16a34a", bg: "#dcfce7", border: "#86efac", value: s.confirmed || 0 },
    { key: "lost",              label: "Lost",            sub: "not proceeding",   icon: <LostIcon />,        accent: "#ef4444", bg: "#fef2f2", border: "#fecaca", value: s.lost || 0 },
  ];
  const operationalKpis: KpiDef[] = [
    { key: "hot",             label: "Hot Leads",       sub: "high intent",      icon: <HotIcon />,        accent: "#ef4444", bg: "#fef2f2", border: "#fecaca", value: stats.hot_leads || 0 },
    { key: "warm",            label: "Warm Leads",      sub: "moderate interest",icon: <WarmIcon />,       accent: "#f97316", bg: "#fff7ed", border: "#fed7aa", value: stats.warm_leads || 0 },
    { key: "cold",            label: "Cold Leads",      sub: "low engagement",   icon: <ColdIcon />,       accent: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", value: stats.cold_leads || 0 },
    { key: "unassigned",      label: "Unassigned",      sub: "needs staff",      icon: <UnassignedIcon />, accent: "#64748b", bg: "#f8fafc", border: "#cbd5e1", value: stats.unassigned || 0 },
    { key: "vip",             label: "VIP Leads",       sub: "priority clients", icon: <VipIcon />,        accent: "#c9a96e", bg: "#fefce8", border: "#fde68a", value: stats.vip_leads || 0 },
    { key: "follow_up_today", label: "Follow-up Today", sub: "due today",        icon: <FollowUpIcon />,   accent: "#dc2626", bg: "#fef2f2", border: "#fecaca", value: stats.follow_up_today || 0 },
  ];
  const allKpis = [...pipelineKpis, ...operationalKpis];

  /* ── Active filter chips ─────────────────────────────────────────────── */
  const activeFilterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (leadView !== "all")
      chips.push({
        key: "leadView",
        label: viewOptions.find(o => o.key === leadView)?.label ?? leadView,
        onRemove: () => { setLeadView(canViewAllLeads ? "all" : "my"); setPage(0); },
      });
    if (activeKpi !== "total")
      chips.push({ key: "kpi", label: allKpis.find(k => k.key === activeKpi)?.label ?? activeKpi, onRemove: () => handleKpi("total") });
    if (statusFilter)
      chips.push({ key: "status", label: STATUS_CONFIG[statusFilter as LeadStatus]?.label ?? statusFilter, onRemove: () => { setStatusFilter(""); setPage(0); } });
    if (sourceFilter)
      chips.push({ key: "source", label: SOURCE_CFG[sourceFilter]?.label ?? sourceFilter, onRemove: () => { setSourceFilter(""); setPage(0); } });
    if (scoreFilter)
      chips.push({ key: "score", label: scoreFilter === "hot" ? "🔥 Hot" : scoreFilter === "warm" ? "☀️ Warm" : "❄️ Cold", onRemove: () => { setScoreFilter(""); setPage(0); } });
    if (assigneeFilter) {
      const u = teamUsers.find(u => String(u.id) === assigneeFilter);
      chips.push({ key: "assignee", label: u ? (u.display_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username) : "Assignee", onRemove: () => { setAssigneeFilter(""); setPage(0); } });
    }
    if (dateField === "event_date")
      chips.push({ key: "dateField", label: "📅 By Event Date", onRemove: () => setDateField("created_at") });
    if (quickDate && dateFrom)
      chips.push({ key: "qd", label: `📅 ${QUICK_DATE_OPTIONS.find(o => o.key === quickDate)?.label}`, onRemove: clearDateFilter });
    else if (!quickDate && (dateFrom || dateTo)) {
      const f = dateFrom ? fmtChipDate(dateFrom) : "";
      const t = dateTo   ? fmtChipDate(dateTo)   : "";
      const rangeLabel = f && t && f !== t ? `${f} → ${t}` : f || t;
      chips.push({ key: "dr", label: `📅 ${rangeLabel}`, onRemove: clearDateFilter });
    }
    if (showUnassigned)
      chips.push({ key: "unassigned", label: "Unassigned Only", onRemove: () => { setShowUnassigned(false); setPage(0); } });
    if (todaysWork)
      chips.push({ key: "todaysWork", label: "🔥 Today's Work", onRemove: () => { setTodaysWork(false); setPage(0); } });
    if (debouncedSearch)
      chips.push({ key: "search", label: `"${debouncedSearch}"`, onRemove: () => { setSearch(""); setDebouncedSearch(""); setPage(0); } });
    return chips;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKpi, statusFilter, sourceFilter, scoreFilter, assigneeFilter, dateField, quickDate, dateFrom, dateTo, showUnassigned, todaysWork, leadView, debouncedSearch, teamUsers]);

  const hasAnyFilter = activeFilterChips.length > 0;
  const selCount = selected.size;

  /* ═══════════════════════════════ RENDER ═════════════════════════════════ */
  return (
    /* ContactActionsProvider hosts the single Call/Email modal at the
       page level so it survives row re-renders. NextActionCell rows
       call `useContactActions()` to get the handlers; the modal lives
       up here and is never unmounted when `loading` flips the table
       to a skeleton during a poll-triggered refetch. */
    <ContactActionsProvider>
    <Box sx={{ p: 3 }}>

      {/* ── Page Header ──────────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY, letterSpacing: "-0.3px" }}>All Leads</Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.2 }}>
            All leads from website, email &amp; manual entry · {meta?.total ?? 0} total · {monthLabel}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: NAVY, borderRadius: "10px", fontWeight: 700, px: 2.5, boxShadow: "0 2px 8px rgba(13,27,42,0.25)", "&:hover": { bgcolor: "#1b2e44" } }}>
            New Lead
          </Button>
          <IconButton onClick={() => { loadLeads(activeKpi); loadStats(); }} sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", p: 1 }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* ── KPI Strip ────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ mb: 2.5, p: 2, borderRadius: "18px", border: "1px solid #e8ecf0", background: "linear-gradient(135deg, #fafbfc 0%, #f4f6f8 100%)", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
        <RowLabel>Pipeline Status</RowLabel>
        <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", mb: 1.8 }}>
          {pipelineKpis.map(d => <KpiCard key={d.key} def={d} active={activeKpi === d.key} loading={statsLoading} onClick={() => handleKpi(d.key)} />)}
        </Box>
        <RowLabel>Score &amp; Operational</RowLabel>
        <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap" }}>
          {operationalKpis.map(d => <KpiCard key={d.key} def={d} active={activeKpi === d.key} loading={statsLoading} onClick={() => handleKpi(d.key)} />)}
        </Box>
      </Paper>

      {/* ══════════════════════════════════════════════════════════════════
          FILTER BAR — sticky, 2-row layout
      ══════════════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{
        p: "12px 16px", mb: 0, border: "1px solid #e5e7eb", borderRadius: "14px 14px 0 0",
        position: "sticky", top: 0, zIndex: 20,
        bgcolor: "rgba(255,255,255,0.97)", backdropFilter: "blur(10px)",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
      }}>
        <Stack spacing={1.2}>

          {/* ── Row 1: Date controls + Actions ─────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>

            {/* Date mode toggle */}
            <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: "8px", p: "2px", flexShrink: 0 }}>
              {(["created_at", "event_date"] as DateField[]).map(df => (
                <Box key={df} onClick={() => { setDateField(df); setPage(0); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 0.5,
                    px: 1.2, py: 0.5, borderRadius: "6px", cursor: "pointer",
                    bgcolor: dateField === df ? "#fff" : "transparent",
                    boxShadow: dateField === df ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    transition: "all 0.14s",
                    color: dateField === df ? NAVY : "#94a3b8",
                  }}>
                  {df === "created_at" ? <LeadDateIcon sx={{ fontSize: 12 }} /> : <EventDateIcon sx={{ fontSize: 12 }} />}
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: dateField === df ? 700 : 500, whiteSpace: "nowrap" }}>
                    {df === "created_at" ? "Lead Date" : "Event Date"}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* Quick date chips */}
            {QUICK_DATE_OPTIONS.map(({ key, label }) => {
              const isActive = quickDate === key;
              return (
                <Chip key={key} size="small" label={label}
                  onClick={() => handleQuickDate(key)}
                  sx={{
                    height: 30, fontSize: "0.7rem", fontWeight: isActive ? 700 : 500,
                    borderRadius: "7px", cursor: "pointer", flexShrink: 0,
                    bgcolor: isActive ? NAVY : "#fff",
                    color: isActive ? "#fff" : "#475569",
                    border: `1.5px solid ${isActive ? NAVY : "#d1d5db"}`,
                    transition: "all 0.13s cubic-bezier(0.4,0,0.2,1)",
                    transform: isActive ? "scale(1.04)" : "scale(1)",
                    boxShadow: isActive ? "0 2px 8px rgba(13,27,42,0.2)" : "none",
                    "& .MuiChip-label": { px: 1.2 },
                    "&:hover": { bgcolor: isActive ? "#1b2e44" : "#f8fafc", borderColor: isActive ? "#1b2e44" : NAVY, color: isActive ? "#fff" : NAVY },
                  }}
                />
              );
            })}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* From / To date inputs — premium calendar picker replaces
                the OS-native <input type="date"> so the filter bar reads
                consistently across browsers and matches the navy/gold
                design system used by the rest of the CRM. The sx prop
                constrains height to 34px so the pair lines up with the
                quick-date chips and other filter controls around them. */}
            <Box sx={{ width: 150, flexShrink: 0 }}>
              <PremiumDatePicker
                label="From"
                placeholder="Start date"
                value={dateFrom}
                onChange={(v) => { setDateFrom(v); setQuickDate(null); setPage(0); }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", height: 34, fontSize: 12.5 } }}
              />
            </Box>
            <Box sx={{ width: 150, flexShrink: 0 }}>
              <PremiumDatePicker
                label="To"
                placeholder="End date"
                value={dateTo}
                onChange={(v) => { setDateTo(v); setQuickDate(null); setPage(0); }}
                minDate={dateFrom || undefined}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", height: 34, fontSize: 12.5 } }}
              />
            </Box>

            {/* Active date range chip */}
            {(dateFrom || dateTo) && (() => {
              const f = dateFrom ? fmtChipDate(dateFrom) : "";
              const t = dateTo   ? fmtChipDate(dateTo)   : "";
              const rangeLabel = f && t && f !== t ? `${f} → ${t}` : f || t;
              return (
                <Chip label={rangeLabel} onDelete={clearDateFilter}
                  deleteIcon={<CloseIcon sx={{ fontSize: "12px !important" }} />}
                  size="small" variant="outlined"
                  sx={{
                    height: 28, fontSize: "0.7rem", fontWeight: 600, borderRadius: "7px",
                    color: NAVY, borderColor: "#c7d2fe", bgcolor: "#eef2ff", flexShrink: 0,
                    "& .MuiChip-deleteIcon": { color: "#818cf8", "&:hover": { color: "#4338ca" } },
                    "& .MuiChip-label": { px: 1.2 },
                  }}
                />
              );
            })()}

            {/* Actions — pushed to the right */}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip icon={<TodaysWorkIcon sx={{ fontSize: 14 }} />} label="Today's Work"
                onClick={() => { setTodaysWork(v => !v); setPage(0); }} size="small"
                sx={{ height: 30, fontWeight: 700, borderRadius: "8px", bgcolor: todaysWork ? "#dc2626" : "#fff", color: todaysWork ? "#fff" : "#dc2626", border: "1.5px solid #dc2626", fontSize: "0.71rem", boxShadow: todaysWork ? "0 2px 8px rgba(220,38,38,0.3)" : "none", transform: todaysWork ? "scale(1.03)" : "scale(1)", transition: "all 0.14s", "& .MuiChip-label": { px: 1.2 }, "&:hover": { bgcolor: todaysWork ? "#b91c1c" : "#fee2e2" } }} />
              <Chip icon={<UnassignedIcon sx={{ fontSize: 13 }} />} label="Unassigned Only"
                onClick={() => { setShowUnassigned(v => !v); setPage(0); }} size="small"
                sx={{ height: 30, fontWeight: showUnassigned ? 700 : 500, borderRadius: "8px", bgcolor: showUnassigned ? "#ef4444" : "#fff", color: showUnassigned ? "#fff" : "#ef4444", border: "1.5px solid #ef4444", fontSize: "0.71rem", boxShadow: showUnassigned ? "0 2px 8px rgba(239,68,68,0.28)" : "none", transform: showUnassigned ? "scale(1.03)" : "scale(1)", transition: "all 0.14s", "& .MuiChip-label": { px: 1.2 }, "&:hover": { bgcolor: showUnassigned ? "#dc2626" : "#fee2e2" } }} />
              {activeKpi !== "total" && (
                <Chip label={allKpis.find(k => k.key === activeKpi)?.label ?? activeKpi} onDelete={() => handleKpi("total")} size="small"
                  sx={{ height: 30, fontWeight: 600, bgcolor: "#f0f4ff", color: NAVY, border: "1px solid #bfdbfe", borderRadius: "8px", fontSize: "0.71rem" }} />
              )}
              {hasAnyFilter && (
                <Chip icon={<CloseIcon sx={{ fontSize: 12 }} />} label="Clear All" size="small" onClick={clearAll}
                  sx={{ height: 30, fontWeight: 600, borderRadius: "8px", bgcolor: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", fontSize: "0.71rem", cursor: "pointer", "&:hover": { bgcolor: "#fee2e2" } }} />
              )}
            </Box>
          </Box>

          {/* ── Row 2: Filters ───────────────────────────────────────────── */}
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>

            {/* Lead view — role-aware tab set.
                Admin / Sales Manager: All / My / Unassigned / Assigned.
                Everyone else: My / Unassigned / Follow-ups / Hot. */}
            <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: "8px", p: "2px", flexShrink: 0 }}>
              {viewOptions.map(opt => (
                <Box key={opt.key} onClick={() => { setLeadView(opt.key); setPage(0); }}
                  sx={{
                    px: 1.3, py: 0.55, borderRadius: "6px", cursor: "pointer",
                    bgcolor: leadView === opt.key ? "#fff" : "transparent",
                    boxShadow: leadView === opt.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                    color: leadView === opt.key ? NAVY : "#94a3b8",
                    transition: "all 0.14s",
                  }}>
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: leadView === opt.key ? 700 : 500, whiteSpace: "nowrap" }}>
                    {opt.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <TextField size="small" placeholder="Search name, email, phone…" value={search}
              onChange={e => handleSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 17 }} /></InputAdornment> }}
              sx={{ width: 240, flexShrink: 0, "& .MuiOutlinedInput-root": { borderRadius: "8px", height: 34 } }} />
            <FormControl size="small" sx={{ minWidth: 148, flexShrink: 0 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" sx={{ borderRadius: "8px", height: 34 }}
                onChange={e => { setStatusFilter(e.target.value); setPage(0); if (e.target.value) setActiveKpi(e.target.value as KpiKey); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: v.color }} />{v.label}</Box></MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 128, flexShrink: 0 }}>
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" sx={{ borderRadius: "8px", height: 34 }}
                onChange={e => { setSourceFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">All Sources</MenuItem>
                {Object.entries(SOURCE_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.icon} {v.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 118, flexShrink: 0 }}>
              <InputLabel>AI Score</InputLabel>
              <Select value={scoreFilter} label="AI Score" sx={{ borderRadius: "8px", height: 34 }}
                onChange={e => { setScoreFilter(e.target.value); setPage(0); }}>
                <MenuItem value="">Any Score</MenuItem>
                <MenuItem value="hot">🔥 Hot</MenuItem>
                <MenuItem value="warm">☀️ Warm</MenuItem>
                <MenuItem value="cold">❄️ Cold</MenuItem>
              </Select>
            </FormControl>
            {/* Cross-staff Assignee filter — admin / superadmin only.
                Non-admin staff are scoped to their own leads server-side
                (LeadService::applyVisibility), so giving them a picker that
                lists every staff member would be misleading: filtering by
                another user's name would always return zero results. */}
            {isAdmin && teamUsers.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 138, flexShrink: 0 }}>
                <InputLabel>Assignee</InputLabel>
                <Select value={assigneeFilter} label="Assignee" sx={{ borderRadius: "8px", height: 34 }}
                  onChange={e => { setAssigneeFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">All Staff</MenuItem>
                  {teamUsers.map(u => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.display_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

        </Stack>
      </Paper>

      {/* ── Active filter summary ─────────────────────────────────────── */}
      {activeFilterChips.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.8, px: 2, py: 1, mb: 0, bgcolor: "#f8fafc", border: "1px solid #e5e7eb", borderTop: "none" }}>
          <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", mr: 0.5 }}>
            Active:
          </Typography>
          {activeFilterChips.map(chip => (
            <Chip key={chip.key} label={chip.label} onDelete={chip.onRemove}
              deleteIcon={<CloseIcon sx={{ fontSize: "11px !important" }} />} size="small"
              sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe", borderRadius: "6px", "& .MuiChip-deleteIcon": { color: "#818cf8", "&:hover": { color: "#4338ca" } } }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={clearAll}
            sx={{ fontSize: "0.68rem", color: "#94a3b8", minWidth: 0, p: "2px 8px", borderRadius: "6px", "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" } }}>
            Clear All
          </Button>
        </Box>
      )}
      <Box sx={{ height: 8, bgcolor: "#f8fafc", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 12px 12px", mb: 1.5 }} />

      {/* ── Bulk Action Bar ─────────────────────────────────────────────
          Desaturated to slate/navy — the bar is functional, not festive.
          A small navy dot replaces the saturated background so the
          selection state reads as an executive notification, not a
          neon banner. */}
      {selCount > 0 && (
        <Paper elevation={0} sx={{
          mb: 1.5, px: 2, py: 1, borderRadius: "10px",
          border: "1px solid #e5e7eb",
          bgcolor: "#fff",
          display: "flex", alignItems: "center", gap: 1.5,
          boxShadow: "0 1px 2px rgba(13,27,42,0.04)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: NAVY }} />
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 12.5 }}>
              {selCount} {selCount === 1 ? "lead" : "leads"} selected
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<StatusChangeIcon sx={{ fontSize: 15 }} />} endIcon={<DropdownIcon sx={{ fontSize: 16 }} />}
            onClick={e => setBulkStatusMenu(e.currentTarget)}
            sx={{
              borderRadius: "8px", fontWeight: 600, fontSize: 12,
              borderColor: "#d1d5db", color: "#475569",
              textTransform: "none", px: 1.2, height: 30,
              "&:hover": { borderColor: NAVY, color: NAVY, bgcolor: "#f8fafc" },
            }}>
            Change Status
          </Button>
          <Menu anchorEl={bulkStatusMenu} open={!!bulkStatusMenu} onClose={() => setBulkStatusMenu(null)}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <MenuItem key={k} onClick={() => handleBulkStatus(k as LeadStatus)}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: v.color }} />{v.label}</Box>
              </MenuItem>
            ))}
          </Menu>
          <Button size="small" variant="text" onClick={() => setSelected(new Set())}
            sx={{ borderRadius: "8px", color: "#94a3b8", fontWeight: 600, fontSize: 12, textTransform: "none", "&:hover": { color: NAVY, bgcolor: "transparent" } }}>
            Deselect
          </Button>
        </Paper>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TABLE — 6-column grouped layout
      ══════════════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>
        <TableContainer>
          <Table sx={{ tableLayout: "fixed" }}>

            {/* Column widths — Next Action widened to host the full button + ⋮ */}
            <colgroup>
              <col style={{ width: 44 }} />            {/* checkbox */}
              <col style={{ width: 230 }} />           {/* Customer */}
              <col style={{ width: 180 }} />           {/* Event */}
              <col style={{ width: 205 }} />           {/* Lead Info */}
              <col style={{ width: 120 }} />           {/* Score */}
              <col style={{ width: 145 }} />           {/* Assigned */}
              <col style={{ width: 215 }} />           {/* Next Action */}
            </colgroup>

            <TableHead>
              <TableRow>
                {/* Checkbox */}
                <TableCell padding="checkbox"
                  sx={{ bgcolor: "#fcfcfd", borderBottom: "1px solid #eef0f3" }}>
                  <Checkbox size="small" checked={isAllSelected} indeterminate={isIndeterminate} onChange={toggleAll} />
                </TableCell>

                {COL_HEADERS.map(col => (
                  <TableCell key={col.label} align={col.align ?? "left"}
                    sx={{
                      // Refined header: subtle background, slate label, no
                      // chunky 2px divider. Reads as a column heading, not
                      // a control bar.
                      fontWeight: 700, color: "#475569", fontSize: 10.5,
                      letterSpacing: 0.6, textTransform: "uppercase",
                      py: 1.4, bgcolor: "#fcfcfd", borderBottom: "1px solid #eef0f3",
                      whiteSpace: "nowrap",
                    }}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} sx={{ color: GOLD }} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    {/* Contextual empty-state:
                        • Restricted user on "My Leads" with 0 rows but
                          unassigned leads available → guide them to the
                          intake pool.
                        • Otherwise → plain "No leads found" with a clear-
                          filters affordance when filters are active. */}
                    {(() => {
                      const unassignedAvailable = Number(stats?.unassigned ?? 0);
                      const showClaimPrompt =
                        !canViewAllLeads &&
                        leadView === "my" &&
                        unassignedAvailable > 0 &&
                        !hasAnyFilter;

                      if (showClaimPrompt) {
                        return (
                          <Stack alignItems="center" spacing={1.2} sx={{ py: 1.5 }}>
                            <Box sx={{
                              width: 44, height: 44, borderRadius: "10px",
                              bgcolor: "#f1f5f9", color: "#64748b",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <UnassignedIcon sx={{ fontSize: 22 }} />
                            </Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                              No assigned leads yet
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                              There {unassignedAvailable === 1 ? "is" : "are"} currently{" "}
                              <Box component="span" sx={{ fontWeight: 700, color: NAVY }}>
                                {unassignedAvailable}
                              </Box>{" "}
                              unassigned lead{unassignedAvailable === 1 ? "" : "s"} available to claim.
                            </Typography>
                            <Button
                              variant="contained"
                              disableElevation
                              size="small"
                              onClick={() => { setLeadView("unassigned"); setPage(0); }}
                              sx={{
                                mt: 0.6, textTransform: "none", fontWeight: 700, fontSize: 12.5,
                                bgcolor: "#0d1b2a", color: "#fff",
                                borderRadius: "8px", px: 2, py: 0.8,
                                boxShadow: "0 4px 12px rgba(13,27,42,0.22)",
                                "&:hover": { bgcolor: "#142b46" },
                              }}
                            >
                              View Unassigned Leads
                            </Button>
                          </Stack>
                        );
                      }

                      return (
                        <Stack alignItems="center" spacing={1}>
                          <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No leads found</Typography>
                          {hasAnyFilter && (
                            <Button size="small" onClick={clearAll} sx={{ fontSize: 12, color: "#6366f1" }}>Clear filters</Button>
                          )}
                        </Stack>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ) : leads.map((lead) => {
                const isSelected    = selected.has(lead.id);
                const isHovered     = hoveredRow === lead.id;
                const isOverdue     = !!(lead.follow_up_required && lead.follow_up_at && lead.follow_up_at < todayStr);
                const isFollowToday = !!(lead.follow_up_required && lead.follow_up_at?.startsWith(todayStr));

                /* Row background — neutral by default; only selection
                   tints, and even that is a quiet slate, not indigo.
                   Freshly arrived (head-check) rows wear a soft amber
                   for ~3s; selection still wins so a user picking the
                   row sees the slate immediately. */
                const isFresh       = freshIds.has(lead.id);
                const rowBg = isSelected ? "#f6f8fb" : isFresh ? "#fff7ed" : "#fff";

                /* Left accent — reserved for the two states a sales
                   operator actually needs to see at a glance. A 2px
                   border feels precise rather than chunky. */
                const leftBorder = isOverdue
                  ? "2px solid #dc2626"
                  : lead.is_vip
                  ? `2px solid ${GOLD}`
                  : "2px solid transparent";

                return (
                  <TableRow
                    key={lead.id}
                    onMouseEnter={() => setHoveredRow(lead.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => openThread(lead.id)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: rowBg,
                      borderLeft: leftBorder,
                      transition: "background 0.12s ease",
                      "& td": {
                        py: "13px", verticalAlign: "top",
                        borderBottom: "1px solid #f1f3f5",
                      },
                      "&:hover td": { bgcolor: isSelected ? "#eef1f6" : "#fafbfc" },
                    }}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox" onClick={e => toggleRow(lead.id, e)}
                      sx={{ bgcolor: "inherit", verticalAlign: "middle !important" }}>
                      <Checkbox size="small" checked={isSelected} />
                    </TableCell>

                    {/* 1. Customer */}
                    <TableCell sx={{ bgcolor: rowBg }}>
                      <CustomerCell lead={lead} isOverdue={isOverdue} />
                    </TableCell>

                    {/* 2. Event */}
                    <TableCell>
                      <EventCell lead={lead} />
                    </TableCell>

                    {/* 3. Lead Info */}
                    <TableCell onClick={e => e.stopPropagation()}>
                      <LeadInfoCell lead={lead} onStatusChange={handleStatusChange} />
                    </TableCell>

                    {/* 4. Score & Source */}
                    <TableCell>
                      <ScoreSourceCell lead={lead} />
                    </TableCell>

                    {/* 5. Assigned — Lead Managers + admins get the
                        "Assign to Me" affordance on unassigned rows.
                        The server enforces the same rule, so even if
                        the button surfaces incorrectly the backend
                        will reject the claim with 403. */}
                    <TableCell>
                      <AssignedCell
                        lead={lead}
                        onClaim={currentUserId ? handleClaim : undefined}
                        claiming={claimingId === lead.id}
                      />
                    </TableCell>

                    {/* 6. Next Action */}
                    <TableCell onClick={e => e.stopPropagation()}>
                      <NextActionCell
                        lead={lead}
                        isHovered={isHovered}
                        onNote={() => setNoteDialog({ open: true, leadId: lead.id })}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination — refined enterprise layout matching the reference:
            "Showing X to Y of Z leads"  ·  Rows per page: 50 ▼  ·  N of M  ·  ‹ ›
            Builds on a plain Select for rows-per-page and two IconButtons
            for prev/next; avoids MUI's noisier TablePagination chrome. */}
        {meta && (() => {
          const total      = meta.total;
          const totalPages = Math.max(1, Math.ceil(total / perPage));
          const firstIdx   = total === 0 ? 0 : page * perPage + 1;
          const lastIdx    = Math.min((page + 1) * perPage, total);
          const canPrev    = page > 0;
          const canNext    = page < totalPages - 1;
          return (
            <Box sx={{
              display: "flex", alignItems: "center",
              px: 2, py: 1, gap: 2,
              borderTop: "1px solid #eef0f3",
              bgcolor: "#fcfcfd",
            }}>
              <Typography sx={{ fontSize: 12, color: "#64748b", flex: 1, fontVariantNumeric: "tabular-nums" }}>
                Showing <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>{firstIdx}</Box>
                {" "}to <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>{lastIdx}</Box>
                {" "}of <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>{total.toLocaleString()}</Box>
                {" "}leads
              </Typography>

              <Stack direction="row" alignItems="center" gap={0.7}>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>Rows per page:</Typography>
                <Select
                  size="small" value={perPage}
                  onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }}
                  sx={{
                    height: 28, fontSize: 12, fontWeight: 600, color: "#1e293b",
                    borderRadius: "6px", bgcolor: "#fff",
                    "& .MuiSelect-select": { py: 0.4, pr: "26px !important", pl: 1.2 },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                  }}
                >
                  {[25, 50, 100].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 12.5 }}>{n}</MenuItem>)}
                </Select>
              </Stack>

              <Typography sx={{ fontSize: 12, color: "#64748b", fontVariantNumeric: "tabular-nums", minWidth: 50, textAlign: "right" }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>{page + 1}</Box>
                {" "}of {totalPages}
              </Typography>

              <Stack direction="row" gap={0.4}>
                <IconButton
                  size="small" disabled={!canPrev}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  sx={{
                    width: 28, height: 28, borderRadius: "6px",
                    border: "1px solid #e5e7eb", bgcolor: "#fff",
                    color: "#475569",
                    "&:hover": { borderColor: NAVY, color: NAVY, bgcolor: "#f8fafc" },
                    "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#eef0f3" },
                  }}
                >
                  <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>‹</Box>
                </IconButton>
                <IconButton
                  size="small" disabled={!canNext}
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  sx={{
                    width: 28, height: 28, borderRadius: "6px",
                    border: "1px solid #e5e7eb", bgcolor: "#fff",
                    color: "#475569",
                    "&:hover": { borderColor: NAVY, color: NAVY, bgcolor: "#f8fafc" },
                    "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#eef0f3" },
                  }}
                >
                  <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>›</Box>
                </IconButton>
              </Stack>
            </Box>
          );
        })()}
      </Paper>

      {/* ── Add Note Dialog ───────────────────────────────────────────── */}
      <Dialog open={noteDialog.open}
        onClose={() => { setNoteDialog({ open: false, leadId: null }); setNoteText(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 1 }}>Add Note</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline rows={3} placeholder="Write your note here…"
            value={noteText} onChange={e => setNoteText(e.target.value)}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setNoteDialog({ open: false, leadId: null }); setNoteText(""); }} sx={{ borderRadius: "8px", color: "#64748b" }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNote} disabled={!noteText.trim() || noteLoading}
            sx={{ borderRadius: "8px", bgcolor: NAVY, fontWeight: 700, "&:hover": { bgcolor: "#1b2e44" } }}>
            {noteLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Thread Drawer ─────────────────────────────────────────────── */}
      <LeadThreadDrawer open={drawerOpen} threadId={selectedId} onClose={() => setDrawerOpen(false)}
        onUpdated={() => { loadLeads(activeKpi); loadStats(); }} />

      {/* ── Create Dialog ─────────────────────────────────────────────── */}
      <CreateLeadDialog open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(mergedLeadId, createdLead) => {
          setCreateOpen(false);
          // Optimistic insert: drop the new lead at the top of the list
          // immediately so the operator sees instant feedback. The
          // refetch below replaces the placeholder with the canonical
          // server payload (with enriched assignee / activity counts).
          if (createdLead && !mergedLeadId) {
            setLeads((prev) => {
              if (prev.some((l) => l.id === createdLead.id)) return prev;
              return [createdLead as LeadThread, ...prev];
            });
            // Bump the "highest seen" markers so the next /check-changes
            // poll doesn't re-fire a "new lead" toast for our own create.
            latestKnownRef.current = {
              id: Math.max(latestKnownRef.current.id, createdLead.id),
              updatedAt: createdLead.updated_at ?? latestKnownRef.current.updatedAt,
            };
          }
          // Snap back to page 1 — the new lead sorts to the top, but if
          // the operator was deep-paginated they wouldn't see it without
          // returning to the first page. This also re-fires the
          // [loadLeads, activeKpi] effect (page is a loadLeads dep) so
          // the canonical data lands without an extra explicit call.
          setPage(0);
          loadStats();
          if (mergedLeadId) { setSelectedId(mergedLeadId); setDrawerOpen(true); }
        }}
        defaultSource="website" />
    </Box>
    </ContactActionsProvider>
  );
}
